import React, { useState } from 'react';

/**
 * The building blocks of the lead details screen.
 *
 * Extracted from the container because the layout rules here are the point of
 * the screen, not incidental to it: what an agent needs at a glance, what they
 * need one click away, and what is reference material they will open twice a
 * year. Keeping them as named components makes that hierarchy visible instead
 * of leaving it implicit in a wall of divs.
 */

/**
 * Type scale.
 *
 * Deliberately a step larger than the admin's default. This is a screen people
 * read all day at arm's length while on a phone call, and the first version
 * set labels at 12px and values at 14px — legible in a design review, tiring
 * in use. Labels stay uppercase for scanning but are no longer the smallest
 * text on the page.
 */
export const LABEL_CLS = 'text-sm uppercase tracking-wide text-neutral-300';
export const VALUE_CLS = 'break-words text-base text-neutral-500';

export const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="py-2">
    <div className={LABEL_CLS}>{label}</div>
    <div className={VALUE_CLS}>{value || '—'}</div>
  </div>
);

export interface FieldSpec {
  label: string;
  value?: React.ReactNode;
}

/**
 * A list of fields with the empty ones dropped.
 *
 * Reference sections carry a lot of fields that are blank on most leads — a
 * lead with no click ids still has a row for them. Rendering every one at full
 * height buries the three that have values, so here an empty field costs
 * nothing and the section says how many it hid.
 */
export const FieldList: React.FC<{ fields: FieldSpec[]; emptyMessage?: string }> = ({
  fields,
  emptyMessage = 'Nothing recorded.'
}) => {
  const filled = fields.filter((field: FieldSpec) => {
    if (field.value === null || field.value === undefined || field.value === '') return false;
    return true;
  });

  if (filled.length === 0) return <p className="text-base text-neutral-400">{emptyMessage}</p>;

  return (
    <>
      {filled.map((field: FieldSpec) => (
        <Field key={field.label} label={field.label} value={field.value} />
      ))}
      {filled.length < fields.length ? (
        <p className="mt-2 text-sm text-neutral-300">
          {fields.length - filled.length} empty field
          {fields.length - filled.length === 1 ? '' : 's'} hidden
        </p>
      ) : null}
    </>
  );
};

/**
 * One cell of the summary strip.
 *
 * The strip exists because the previous layout gave a phone number and a
 * campaign id the same visual weight. These are the handful of values an agent
 * acts on — reaching the person, knowing whose lead it is and how long they
 * have — so they are bigger than everything below and sit above the fold.
 */
export const SummaryCell: React.FC<{
  label: string;
  value?: React.ReactNode;
  href?: string;
  emphasis?: boolean;
}> = ({ label, value, href, emphasis }) => (
  <div className="min-w-0 px-4 py-3">
    <div className={LABEL_CLS}>{label}</div>
    <div
      className={`mt-0.5 truncate ${
        emphasis ? 'font-gilroy-bold text-lg text-neutral-500' : 'text-base text-neutral-500'
      }`}
      title={typeof value === 'string' ? value : undefined}
    >
      {/* A phone number on an ops screen is there to be dialled, and an email
          to be written to. Plain text makes both a copy-paste job. */}
      {value ? (
        href ? (
          <a className="text-primary-600 underline decoration-primary-200" href={href}>
            {value}
          </a>
        ) : (
          value
        )
      ) : (
        <span className="text-neutral-300">—</span>
      )}
    </div>
  </div>
);

export const SummaryStrip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="grid grid-cols-2 divide-creams-600 rounded-2xl border border-creams-600 bg-white shadow-md sm:grid-cols-3 sm:divide-x lg:grid-cols-6">
    {children}
  </section>
);

/**
 * A card. `collapsible` sections start closed and are the reference material —
 * attribution, first touch, the audit trail. They were taking the top-right of
 * the screen while the notes an agent writes on every call were squeezed
 * beside them.
 */
export const Card: React.FC<{
  title: string;
  accent?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  subtitle?: string;
  children: React.ReactNode;
}> = ({
  title,
  accent = 'bg-primary-400',
  collapsible,
  defaultOpen = false,
  subtitle,
  children
}) => {
  const [open, setOpen] = useState(!collapsible || defaultOpen);

  return (
    <div className="rounded-2xl border border-creams-600 bg-white p-5 shadow-md">
      <h2 className="flex items-center gap-2 font-gilroy-bold text-lg text-neutral-500">
        <span className={`h-5 w-1 rounded-full ${accent}`} aria-hidden="true" />
        {title}
        {collapsible ? (
          <button
            type="button"
            className="ml-auto text-sm font-gilroy-medium text-primary-600"
            aria-expanded={open}
            onClick={() => setOpen((current: boolean) => !current)}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        ) : null}
      </h2>
      {subtitle && !open ? <p className="mt-1 text-sm text-neutral-300">{subtitle}</p> : null}
      {open ? <div className="mt-3">{children}</div> : null}
    </div>
  );
};
